/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Link } from 'gatsby';
import FormControl from './components/FormControl';
import FormSelect from './components/FormSelect';
import CallToAction from '../../CallToAction';
import {
  handleSubmit,
  validateOnChange,
  handlePhoneOnChange,
} from './utils/helpers';
import formatUrlValue from '../Utilities/formatUrlValue';
import truncateUrl from '../Utilities/truncateUrl';
import { getUrlParam } from '../Utilities/getUrlParam';
import {
  Container,
  Header,
  Form,
  TwoColumn,
  Button,
  Footer,
  FooterLink,
} from './styles/index.styled';

import { useLightbox } from '../../../context/lightboxContext';

const DemoForm = ({ component, location }) => {
  const language = location.pathname.split('/').includes('es') ? 'es' : 'en';
  const { closeLightbox } = useLightbox();
  const { t } = useTranslation();
  const [data, setData] = useState({
    email: '',
    firstname: '',
    lastname: '',
    phone: '',
    companyname: '',
    Number_of_Technicians_Range__c: '',
    url: truncateUrl(location.href),
    jobtitle: '',
    industry: '',
    country: null,
    countrycode: '',
    state: null,
    gclid: '',
    language: component?.language || '',
    offer: component?.offer || '',
  });
  const [validation, setValidation] = useState({
    email: '',
    firstname: '',
    lastname: '',
    phone: '',
    companyname: '',
    Number_of_Technicians_Range__c: '',
  });

  useEffect(() => {
    const url = formatUrlValue(location);

    setData(prevData => ({
      ...prevData,
      url: truncateUrl(url),
      gclid: getUrlParam('gclid'),
    }));
  }, []);

  const handleChange = e => {
    if (e.target.name === 'phone') {
      handlePhoneOnChange(e, validation, setValidation, data, setData);
      return;
    }
    setData({ ...data, [e.target.name]: e.target.value });
    validateOnChange(e, e.target.name, validation, setValidation);
  };

  const requiredFields = [
    'email',
    'firstname',
    'lastname',
    'phone',
    'companyname',
    'Number_of_Technicians_Range__c',
  ];

  const hasErrors = Object.values(validation).some(value => value !== '');
  const hasEmptyRequiredFields = requiredFields.some(field => !data[field]);
  const isDisabled = hasErrors || hasEmptyRequiredFields;

  return (
    <Container>
      <Header>{t('Get a Free Product Tour')}</Header>
      <Form
        id="demo-form"
        data-zi-mapped-form
        onSubmit={e => {
          handleSubmit({
            e,
            data,
            setData,
            validation,
            setValidation,
            customFormHandler: 'a4c04d1a-78bb-47db-a8ca-ccee5bcdc003',
            closeLightbox,
            chiliPiperRouterId: component?.chiliPiperRouterId,
          });
        }}
      >
        <FormControl
          id="email"
          type="email"
          name="email"
          placeholder={t('Enter your business email address')}
          label={t('Business Email Address')}
          required
          autoComplete="email"
          errorMessage={validation.email}
          value={data.email}
          handleChange={handleChange}
        />
        <TwoColumn>
          <FormControl
            id="firstname"
            type="text"
            name="firstname"
            placeholder={t('Enter your first name')}
            label={t('First Name')}
            autoComplete="given-name"
            required
            errorMessage={validation.firstname}
            value={data.firstname}
            handleChange={handleChange}
          />
          <FormControl
            id="lastname"
            type="text"
            name="lastname"
            placeholder={t('Enter your last name')}
            label={t('Last Name')}
            autoComplete="family-name"
            required
            errorMessage={validation.lastname}
            value={data.lastname}
            handleChange={handleChange}
          />
        </TwoColumn>
        <FormControl
          id="phone"
          type="tel"
          name="phone"
          placeholder={t('Enter your mobile number')}
          label={t('Mobile Number')}
          autoComplete="off"
          required
          errorMessage={validation.phone}
          value={data.phone}
          handleChange={handleChange}
        />
        <FormControl
          id="companyname"
          type="text"
          name="companyname"
          placeholder={t('Enter your company name')}
          label={t('Company Name')}
          autoComplete="organization"
          required
          errorMessage={validation.companyname}
          value={data.companyname}
          handleChange={handleChange}
        />
        <FormSelect
          id="Number_of_Technicians_Range__c"
          name="Number_of_Technicians_Range__c"
          label={t('Number of Technicians')}
          placeholder={t('Select the number of technicians')}
          value={data.Number_of_Technicians_Range__c}
          handleChange={handleChange}
          errorMessage={validation.Number_of_Technicians_Range__c}
          options={[
            { value: '', label: t('Select the number of technicians') },
            { value: '1-19', label: '1-19' },
            { value: '20-39', label: '20-39' },
            { value: '40+', label: '40+' },
          ]}
          helperText="A technician is a member of maintenance staff that manage tasks like work orders, asset info, inventory, etc."
          required
        />
        <FormControl
          id="industry"
          type="text"
          name="industry"
          placeholder="Industry"
          label="Industry"
          autoComplete="on"
          value={data.industry}
          handleChange={handleChange}
          hidden
        />
        <FormControl
          id="country"
          type="text"
          name="country"
          label="Country"
          placeholder="Country"
          autoComplete="country"
          value={data.country}
          handleChange={handleChange}
          hidden
        />
        <FormControl
          id="state"
          type="text"
          name="state"
          label="State"
          placeholder="State"
          autoComplete="state"
          value={data.state}
          handleChange={handleChange}
          hidden
        />
        <input type="text" id="gclid" name="gclid" value={data.gclid} />
        <input type="text" id="Language" name="Language" value={language} />
        <Button type="submit" className={`${isDisabled ? 'disabled' : ''}`}>
          {t('Request Free Product Tour')}
        </Button>
      </Form>
      <Footer>
        <FooterLink>
          <p>{t('Not Ready Yet?')}</p>
          <CallToAction
            variant="Link - Blue"
            size="sm"
            url="https://www.upkeep.com/free-trial-signup"
            label={t('Sign up for free')}
          />
        </FooterLink>
        <div className="use-policy">
          <p>{t('No Credit Card Required.')}</p>
          <p>
            {t('By clicking above, you agree to the')}{' '}
            <Link to="/terms">{t('UpKeep Terms of Use')}.</Link>
          </p>
        </div>
      </Footer>
    </Container>
  );
};

export default DemoForm;
